import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { AppLayout } from 'src/components/layout/app-layout';
import { NavbarTab } from 'src/components/layout/app-navbar';
import { RootState } from 'src/redux/redux-store';
import { isOrgAdmin, isSuperAdmin } from 'src/services/permission/permission.helpers';
import ReportsTab from './tabs/reports.tab';
import InsightsTab from './tabs/insights.tab';
import ReportingBetaTab from './tabs/reporting-beta.tab';

const ReportsPage = () => {
  useSetPageTitle('Reports');
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const userState = useSelector((state: RootState) => state.userState);
  const hasBetaTab = isOrgAdmin(userState.permissions) || isSuperAdmin(userState.permissions);

  const reportsTabs: NavbarTab[] = [
    {
      id: '',
      key: 'ALL',
      label: 'Reports',
    },
    ...(hasBetaTab
      ? [{ id: 'reporting-beta' as const, key: 'reporting-beta' as const, label: 'Reporting (Beta)' }]
      : []),
  ];

  const { tab } = useParams<{ tab?: string }>();

  const validTabs = reportsTabs.map((reportTab) => reportTab.id);
  type TabType = typeof validTabs[number];

  const currentTab: TabType = validTabs.includes(tab as TabType)
    ? (tab as TabType)
    : '';

  useEffect(() => {
    if (tab === 'reporting-beta' && !hasBetaTab) {
      navigate(clientId ? `/client/${clientId}/reports` : '/reports', { replace: true });
    }
  }, [tab, hasBetaTab, navigate, clientId]);

  const handleTabChange = (tabId: string) => {
    if (clientId) {
      navigate(`/client/${clientId}/reports/${tabId}`);
    } else {
      navigate(`/reports/${tabId}`);
    }
  };

  return (
    <AppLayout
      activePage="/client/reports"
      navbarProps={{
        showBackButton: false,
        backPath: clientId ? `/client/${clientId}/reports` : '/reports',
        tabs: reportsTabs,
        activeTab: currentTab,
        onTabChange: handleTabChange,
      }}
    >
      <div className="h-[calc(100vh-8rem)]">
        {currentTab === '' && <ReportsTab showOldVersionButton={true} />}
        {currentTab === 'insights' && <InsightsTab />}
        {currentTab === 'reporting-beta' && <ReportingBetaTab />}
      </div>
    </AppLayout>
  );
};

export default ReportsPage;
